import { observable, action, makeObservable } from 'mobx'
import {
  lead_intake_vendors_path,
  lead_intake_success_path,
  search_with_params_vendors_path
} from '../../../routes.js.erb'

export type FavoriteConnect = 'whatsapp' | 'phone'

interface IOrderInfo {
    client_name: string
    client_country: string
    client_countryCode: string
    client_phone: string
    wishes: string
    deadlines: string
    additional: string
    favorite_connect: FavoriteConnect
}

export interface IVendorsLeadIntakeModel {
    currentStep: number
    setCurrentStep: (newStep: number) => void
    intakeOptions: string[]
    selectedIntakeOption: string
    setIntakeOption: (id: string) => void
    orderInfo: IOrderInfo
    errors: Record<string, string>
    serverPath: string
    allVendorsPath: string
    locale: string
    locationSlug: string
    categorySlug: string
    isLoading: boolean
    isInputEmpty: boolean
    isPhoneEmpty: boolean
    setClientName: (name: string) => void
    setClientPhone: (phone: string) => void
    setClientCountryCode: (countryCode: string) => void
    setOrderWishes: (wish: string) => void
    setOrderDeadlines: (deadlines: string) => void
    setOrderAdditional: (additional: string) => void
    setFavoriteConnect: (connectType: FavoriteConnect) => void
    saveLeadIntakeOnServer: () => void
    setIntl: (intl: any) => void
    setIsLoading: (isLoading: boolean) => void
    setIsInputEmpty: (isEmpty: boolean) => void
    setIsPhoneEmpty: (isEmpty: boolean) => void
}

class VendorsLeadIntakeModel implements IVendorsLeadIntakeModel {
  currentStep: number
  serverPath: string
  allVendorsPath: string
  locale: string
  locationSlug: string
  categorySlug: string
  intakeOptions: string[]
  selectedIntakeOption: string
  orderInfo: IOrderInfo
  errors: Record<string, string> = {}
  intl: any
  isLoading: boolean
  isInputEmpty: boolean
  isPhoneEmpty: boolean

  constructor ({ locale, locationCountryCode, locationCountry, locationSlug, categorySlug }: {
        locale: string
        locationCountry: string
        locationCountryCode: string
        locationSlug: string
        categorySlug: string
    }) {
    this.currentStep = 0
    this.intakeOptions =
            ['printing_intake', 'branding_intake', 'special_intake'] // Get from props then

    this.locale = locale
    this.locationSlug = locationSlug
    this.categorySlug = categorySlug
    this.orderInfo = {
      client_name: '',
      client_country: locationCountry,
      client_countryCode: locationCountryCode,
      client_phone: '',
      wishes: '',
      deadlines: '',
      additional: '',
      favorite_connect: 'whatsapp'
    }

    this.selectedIntakeOption = this.intakeOptions[0]
    this.serverPath = lead_intake_vendors_path(this.locale, this.locationSlug, this.categorySlug)
    this.allVendorsPath = search_with_params_vendors_path(this.locale, this.locationSlug, this.categorySlug)
    this.intl = false

    this.isLoading = false
    this.isInputEmpty = false
    this.isPhoneEmpty = false

    makeObservable(this, {
      currentStep: observable,
      intakeOptions: observable,
      selectedIntakeOption: observable,
      isLoading: observable,
      orderInfo: observable,
      errors: observable,
      isInputEmpty: observable,
      isPhoneEmpty: observable,
      setCurrentStep: action,
      setIntakeOption: action,
      setClientName: action,
      setClientPhone: action,
      setClientCountryCode: action,
      setOrderWishes: action,
      setOrderDeadlines: action,
      setOrderAdditional: action,
      setFavoriteConnect: action,
      saveLeadIntakeOnServer: action,
      setIntl: action,
      setIsLoading: action,
      setIsInputEmpty: action,
      setIsPhoneEmpty: action
    })
  }

  setIntl (intl: any) {
    if (!this.intl) {
      this.intl = intl
    }
  }

  setCurrentStep (newStep: number) {
    if (newStep != this.currentStep) {
      if (ahoy) {
        ahoy.track('VendorsLeadIntakeModel setCurrentStep ' + newStep)
      }
      this.currentStep = newStep
    }
  }

  setIsLoading (isLoading: boolean) {
    this.isLoading = isLoading
  }

  setIsInputEmpty (isEmpty: boolean) {
    this.isInputEmpty = isEmpty
  }

  setIsPhoneEmpty (isEmpty: boolean) {
    this.isPhoneEmpty = isEmpty
  }

  setIntakeOption (id: string) {
    this.selectedIntakeOption = id
  }

  setClientName (name: string) {
    this.orderInfo.client_name = name
    delete this.errors.name
  }

  setClientCountryCode (countryCode: string) {
    this.orderInfo.client_countryCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`
  }

  setClientPhone (phone: string) {
    this.orderInfo.client_phone = phone
    delete this.errors.phone
  }

  setOrderWishes (wish: string) {
    this.orderInfo.wishes = wish
  }

  setOrderDeadlines (deadlines: string) {
    this.orderInfo.deadlines = deadlines
  }

  setOrderAdditional (additional: string) {
    this.orderInfo.additional = additional
  }

  setFavoriteConnect (connectType: FavoriteConnect) {
    this.orderInfo.favorite_connect = connectType
  }

  saveLeadIntakeOnServer () {
    this.setIsLoading(true)

    const orderInfoFormData = new FormData()
    const translatedIntakeOption = this.intl.formatMessage({
      id: this.selectedIntakeOption === 'another' ? 'another' : `intake.keys.${this.selectedIntakeOption}_title`,
      defaultMessage: 'Printing of materials'
    })
    orderInfoFormData.append('user_input[intake_category]', translatedIntakeOption)
    orderInfoFormData.append('user_input[client_name]', this.orderInfo.client_name)
    orderInfoFormData.append('user_input[client_country_code]', this.orderInfo.client_countryCode)
    orderInfoFormData.append('user_input[client_phone]', this.orderInfo.client_phone)
    orderInfoFormData.append('user_input[wishes]', this.orderInfo.wishes)
    orderInfoFormData.append('user_input[deadlines]', this.orderInfo.deadlines)
    orderInfoFormData.append('user_input[additional]', this.orderInfo.additional)
    orderInfoFormData.append('user_input[favorite_connect]', this.orderInfo.favorite_connect)

    fetch(this.serverPath, {
      method: 'POST',
      body: orderInfoFormData
    })
      .then(response => response.json().then(data => {
        if (response.ok) {
          this.navigateToSuccessPage(data.lead_intake.id)
        } else if (response.status === 422) {
          this.errors = data.errors || {}
          this.setIsLoading(false)
        } else {
          this.setIsLoading(false)
          throw new Error('Error: Something went wrong')
        }
      }))
      .catch(error => {
        console.error('Server error:', error)
      })
  }

  navigateToSuccessPage (intakeId: string) {
    location.href = lead_intake_success_path(this.locale, intakeId)
  }
}

export default VendorsLeadIntakeModel
